import React, { useState } from "react";
import { Section, SerifH, Eyebrow, PrimaryButton, Card } from "../components/ui-kit/Primitives";
import { Mail, MapPin, Calendar, Send } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";

const initial = {
  name: "",
  email: "",
  phone: "",
  organization: "",
  service_interest: "consulting",
  stage: "",
  message: "",
};

const SERVICES = [
  { v: "consulting", l: "Consulting for my agency" },
  { v: "peer_support", l: "Peer support services" },
  { v: "partnership", l: "Partnership / community organization" },
  { v: "workshop", l: "Request a workshop" },
  { v: "other", l: "Something else" },
];

const STAGES = [
  "Just exploring the idea",
  "Forming the business",
  "Pre-launch / building structure",
  "Operating but need better systems",
  "Pursuing credentialing / NCTracks",
  "Seeking funding or partnerships",
  "I'm an individual seeking support",
];

const Contact = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      setForm(initial);
      toast.success("Thank you — we'll be in touch shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Section className="pt-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <Eyebrow>Contact · Book Consultation</Eyebrow>
            <SerifH as="h1" className="text-5xl sm:text-6xl">
              Let us build the right pathway forward.
            </SerifH>
            <p className="mt-6 text-lg text-slate-600 font-light leading-relaxed max-w-2xl">
              Whether you are starting a peer support agency, improving your current organization,
              preparing for credentialing, or seeking community wellness support — KBX Pathways To
              Wellness is ready to connect with you.
            </p>
          </div>
          <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Card className="p-6">
              <Mail className="w-5 h-5 text-brand-green" />
              <div className="mt-3 text-xs tracking-[0.2em] uppercase text-brand-sage">Email</div>
              <a
                href="mailto:info@kbxpathwaystowellness.com"
                className="block mt-1 font-serif text-lg text-brand-green hover:underline"
              >
                info@kbxpathwaystowellness.com
              </a>
            </Card>
            <Card className="p-6">
              <MapPin className="w-5 h-5 text-brand-green" />
              <div className="mt-3 text-xs tracking-[0.2em] uppercase text-brand-sage">Location</div>
              <div className="mt-1 font-serif text-lg text-brand-green">Raleigh, North Carolina</div>
            </Card>
          </div>
        </div>
      </Section>

      <Section className="pt-0">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Form */}
          <div className="lg:col-span-7">
            <Card className="p-8 sm:p-10" data-testid="contact-form-card">
              {done ? (
                <div data-testid="contact-success" className="py-16 text-center">
                  <div className="w-14 h-14 rounded-full bg-brand-mist mx-auto flex items-center justify-center text-brand-green">
                    <Send className="w-6 h-6" />
                  </div>
                  <SerifH as="h3" className="text-3xl mt-5">Thank you.</SerifH>
                  <p className="text-slate-600 mt-3 max-w-md mx-auto">
                    We received your message. A member of the KBX team will be in touch soon.
                  </p>
                  <button
                    onClick={() => setDone(false)}
                    className="mt-6 text-sm text-brand-green underline underline-offset-4"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="name">Name</Label>
                      <Input id="name" data-testid="contact-name" value={form.name} onChange={(e) => update("name", e.target.value)} required />
                    </div>
                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input id="email" type="email" data-testid="contact-email" value={form.email} onChange={(e) => update("email", e.target.value)} required />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="phone">Phone</Label>
                      <Input id="phone" data-testid="contact-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} />
                    </div>
                    <div>
                      <Label htmlFor="organization">Organization</Label>
                      <Input id="organization" data-testid="contact-organization" value={form.organization} onChange={(e) => update("organization", e.target.value)} />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <Label>Are you seeking consulting or peer support?</Label>
                      <Select value={form.service_interest} onValueChange={(v) => update("service_interest", v)}>
                        <SelectTrigger data-testid="contact-service-interest" className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SERVICES.map((s) => (
                            <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>What stage are you in?</Label>
                      <Select value={form.stage} onValueChange={(v) => update("stage", v)}>
                        <SelectTrigger data-testid="contact-stage" className="mt-1">
                          <SelectValue placeholder="Select stage" />
                        </SelectTrigger>
                        <SelectContent>
                          {STAGES.map((s) => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="message">Tell us more about what you need</Label>
                    <Textarea id="message" data-testid="contact-message" rows={5} value={form.message} onChange={(e) => update("message", e.target.value)} required />
                  </div>
                  <div className="pt-2">
                    <button
                      type="submit"
                      data-testid="contact-submit"
                      disabled={submitting}
                      className="inline-flex items-center justify-center gap-2 bg-brand-green text-white rounded-full px-7 py-3.5 text-sm font-medium tracking-wide hover:bg-brand-navy transition-all disabled:opacity-60"
                    >
                      {submitting ? "Sending…" : "Send Message"}
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </form>
              )}
            </Card>
          </div>

          {/* Booking column */}
          <div className="lg:col-span-5">
            <Card className="p-8 bg-brand-green text-white border-brand-green">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-brand-gold" />
                <div className="text-xs tracking-[0.25em] uppercase text-brand-gold">Book directly</div>
              </div>
              <SerifH as="h3" className="text-3xl text-white mt-3">
                Pick a time on our calendar.
              </SerifH>
              <p className="text-slate-300 mt-3 font-light leading-relaxed">
                Prefer to schedule a strategy call directly? Use our booking calendar below or reach
                out via the form — whichever works best for you.
              </p>
              <div className="mt-6 rounded-xl overflow-hidden bg-white">
                <iframe
                  title="Calendly"
                  data-testid="calendly-iframe"
                  src="https://calendly.com/kbxpathwaystowellness-info/30min?embed_domain=kbxpathwaystowellness.com&embed_type=Inline"
                  className="w-full"
                  style={{ height: 560, border: 0 }}
                />
              </div>
              <p className="text-xs text-slate-400 mt-3">
                If the calendar is not loading, email{" "}
                <a className="underline" href="mailto:info@kbxpathwaystowellness.com">
                  info@kbxpathwaystowellness.com
                </a>{" "}
                and we will send available times.
              </p>
            </Card>

            <Card className="p-7 mt-6">
              <Eyebrow>Other ways to connect</Eyebrow>
              <ul className="space-y-3 text-slate-700">
                <li>• Book a Consultation</li>
                <li>• Request Consulting Information</li>
                <li>• Ask About Peer Support Services</li>
                <li>• Partner With Us</li>
                <li>• Request a Workshop</li>
              </ul>
            </Card>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default Contact;
